import React, { memo, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { SparklesSolid } from "@UI/Components/Icons/SparklesSolid";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { Text } from "@UI/Components/Text/Text";
import useThemeToken from "@UI/hooks/useThemeToken";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import messages from "../../messages";

/** One banner per screen, so a module-level id cannot collide with itself. */
const SPARKLE_GRADIENT_ID = "rtPrefillBannerSparkleGradient";

/**
 * Figma's border colour. No theme token matches it — same situation as
 * `SuggestionBadge`'s #FFE4DC, so it is a literal hex here too.
 */
const BORDER_COLOR = "#ff787f";

/**
 * "We filled this in from your description" banner (RT-4321).
 *
 * Owns no screen-specific logic: it renders fixed copy and dismisses itself, so
 * whichever screen the visitor lands on first can drop it in unconditionally —
 * the subcategory picker for a picker visitor, the first question screen for an
 * SEM one, which skips the picker.
 */
export const PrefillBanner = memo(() => {
  const { formatMessage } = useI18n();
  // ponytail: dismissal is not sticky across a reload. Wire it to
  // `STORAGE_KEYS.DISMISSED_HINTS` if it needs to survive one.
  const [isDismissed, setIsDismissed] = useState(false);

  // Destructured: `useThemeToken` returns string[], and `stopColor` needs a
  // scalar.
  const [pink500, orange500] = useThemeToken("colors", [
    "pink.500",
    "orange.500",
  ]);

  if (isDismissed) return null;

  return (
    <Flex
      gap="8px"
      alignItems="flex-start"
      padding="8px 10px"
      borderRadius="8px"
      border="1px solid"
      borderColor={BORDER_COLOR}
      background="white"
    >
      {/* Gradient on an SVG path needs a paint server — `bgClip: text` works on
          text, not on a path's fill. Same trick as `DescriptionField`. */}
      <svg
        width="0"
        height="0"
        aria-hidden
        focusable="false"
        style={{ position: "absolute", width: 0, height: 0 }}
      >
        <defs>
          {/* Figma's stop is at 94.156deg. objectBoundingBox gradients take
              fractional x/y rather than an angle, so this approximates it. */}
          <linearGradient
            id={SPARKLE_GRADIENT_ID}
            x1="45%"
            y1="0%"
            x2="55%"
            y2="100%"
          >
            <stop offset="0%" stopColor={pink500} />
            <stop offset="99.871%" stopColor={orange500} />
          </linearGradient>
        </defs>
      </svg>
      <Icon
        as={SparklesSolid}
        width="14px"
        height="14px"
        // Centres the icon on the text's first line: (14px × 1.5 − 14px) / 2.
        marginTop="3.5px"
        flexShrink={0}
        aria-hidden
        sx={{ path: { fill: `url(#${SPARKLE_GRADIENT_ID})` } }}
      />
      <Box flex="1">
        <Text fontSize="sm" fontWeight="bold" lineHeight="1.5" color="gray.800">
          <FormattedMessage
            {...messages["taskCreation.prefill.banner.title"]}
          />
        </Text>
        <Text fontSize="sm" lineHeight="1.5" color="gray.700">
          <FormattedMessage {...messages["taskCreation.prefill.banner.body"]} />
        </Text>
      </Box>
      <Box
        as="button"
        type="button"
        aria-label={formatMessage(
          messages["taskCreation.prefill.banner.dismiss"]
        )}
        onClick={() => setIsDismissed(true)}
        flexShrink={0}
        lineHeight="0"
      >
        <Icon as={Xmark} width="12px" height="12px" color="gray.800" />
      </Box>
    </Flex>
  );
});

PrefillBanner.displayName = "PrefillBanner";
